import React from "react";
import { TrendingUp, TrendingDown, Calendar } from "lucide-react";

interface ReportingPeriod {
  reportingPeriodId: string;
  period: string;
}

interface GroupedData {
  currentTotal: number;
  comparisonTotal: number;
}

interface PeriodSummaryProps {
  selectedPeriod: ReportingPeriod | null;
  comparisonPeriod: ReportingPeriod | null;
  groupedData: GroupedData;
  getPeriodTransactionCount: (periodId: string) => number;
}

export const PeriodSummary: React.FC<PeriodSummaryProps> = ({
  selectedPeriod,
  comparisonPeriod,
  groupedData,
  getPeriodTransactionCount,
}) => {
  if (!selectedPeriod) return null;

  const formatValue = (value: number) => {
    return (value >= 0 ? '' : '-') + '$' + Math.abs(value).toLocaleString();
  };

  const netChange = groupedData.currentTotal - groupedData.comparisonTotal;

  return (
    <div className="mt-6 p-4 border rounded-lg bg-gray-50">
      <h4 className="font-semibold mb-2 flex items-center gap-2">
        <Calendar className="h-4 w-4" />
        Period Summary
      </h4>
      <div className="grid grid-cols-2 gap-6 text-sm">
        <div className="space-y-2">
          <div className="font-semibold text-gray-900">Current Period</div>
          <div className="text-gray-700">
            <span className="font-medium">Transactions:</span> {getPeriodTransactionCount(selectedPeriod.reportingPeriodId)}
          </div>
          <div className="text-gray-700">
            <span className="font-medium">Total Value:</span> 
            <span className={`font-mono ml-2 ${groupedData.currentTotal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatValue(groupedData.currentTotal)}
            </span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="font-semibold text-gray-900">
            {comparisonPeriod ? 
              `Comparison Period` :
              "Previous Period"
            }
            {comparisonPeriod && (
              <div className="text-xs font-normal text-gray-500">
                {new Date(comparisonPeriod.period).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="text-gray-700">
            <span className="font-medium">Total Value:</span>
            <span className={`font-mono ml-2 opacity-75 ${groupedData.comparisonTotal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatValue(groupedData.comparisonTotal)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">Net Change:</span>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold ${
              netChange > 0 
                ? 'bg-emerald-100 text-emerald-800' 
                : netChange < 0
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {netChange > 0 && <TrendingUp className="h-4 w-4" />}
              {netChange < 0 && <TrendingDown className="h-4 w-4" />}
              <span className="font-mono">
                {netChange >= 0 ? '+' : '-'}${Math.abs(netChange).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 